import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { User, Camera, TrendingDown, TrendingUp, Target, Calendar, Activity, Upload, Lock, Eye, EyeOff } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const ProfilePage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    bio: '',
    date_of_birth: '',
    gender: '',
    height_cm: '',
    current_weight_kg: '',
    target_weight_kg: '',
    fitness_goal: '',
    fitness_level: ''
  });

  const [weightEntry, setWeightEntry] = useState({
    weight_kg: '',
    notes: ''
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to access profile');
      navigate('/login');
      return;
    }
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      const [profileRes, progressRes] = await Promise.all([
        api.get('/profile'),
        api.get('/profile/progress')
      ]);
      setProfile(profileRes.data);
      setProgress(progressRes.data);
      setFormData({
        full_name: profileRes.data.full_name || '',
        phone: profileRes.data.phone || '',
        bio: profileRes.data.bio || '',
        date_of_birth: profileRes.data.date_of_birth || '',
        gender: profileRes.data.gender || '',
        height_cm: profileRes.data.height_cm || '',
        current_weight_kg: profileRes.data.current_weight_kg || '',
        target_weight_kg: profileRes.data.target_weight_kg || '',
        fitness_goal: profileRes.data.fitness_goal || '',
        fitness_level: profileRes.data.fitness_level || ''
      });
    } catch (error) {
      toast.error('Failed to load profile');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      await api.patch('/profile', formData);
      toast.success('Profile updated successfully!');
      setEditMode(false);
      fetchProfileData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogWeight = async () => {
    if (!weightEntry.weight_kg) {
      toast.error('Please enter weight');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/profile/weight', null, {
        params: {
          weight_kg: parseFloat(weightEntry.weight_kg),
          notes: weightEntry.notes || undefined
        }
      });
      toast.success('Weight logged successfully!');
      setWeightEntry({ weight_kg: '', notes: '' });
      fetchProfileData();
    } catch (error) {
      toast.error('Failed to log weight');
    } finally {
      setLoading(false);
    }
  };

  const handleProfilePictureUpdate = async (url) => {
    try {
      await api.post('/profile/picture', null, { params: { picture_url: url } });
      toast.success('Profile picture updated!');
      fetchProfileData();
    } catch (error) {
      toast.error('Failed to update profile picture');
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await api.post(`/auth/change-password?current_password=${encodeURIComponent(passwordData.currentPassword)}&new_password=${encodeURIComponent(passwordData.newPassword)}`);
      toast.success('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPEG, PNG, WebP, or GIF');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/profile/upload-picture', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Profile picture uploaded!');
      fetchProfileData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const calculateBMI = () => {
    if (profile?.height_cm && profile?.current_weight_kg) {
      const heightM = profile.height_cm / 100;
      const bmi = profile.current_weight_kg / (heightM * heightM);
      return bmi.toFixed(1);
    }
    return null;
  };

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-foreground">Loading profile...</div>
      </div>
    );
  }

  const bmi = calculateBMI();

  return (
    <div className="min-h-screen bg-background p-6 md:p-12">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-heading font-black uppercase tracking-tight mb-2" data-testid="profile-title">
              My Profile
            </h1>
            <p className="text-muted-foreground">Manage your personal information and track progress</p>
          </div>
          <Link to="/dashboard">
            <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
              Back to Dashboard
            </Button>
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Summary Card */}
          <div className="lg:col-span-1">
            <Card className="bg-card/50 border-white/10">
              <CardContent className="pt-6">
                <div className="flex flex-col items-center">
                  <div className="relative mb-4">
                    <div className="w-32 h-32 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center overflow-hidden">
                      {profile.profile_picture ? (
                        <img src={profile.profile_picture} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-16 h-16 text-white" />
                      )}
                    </div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      data-testid="profile-picture-input"
                    />
                    <Button
                      size="sm"
                      className="absolute bottom-0 right-0 rounded-full w-10 h-10 p-0"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPicture}
                      data-testid="change-picture-btn"
                    >
                      {uploadingPicture ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  
                  <h2 className="text-2xl font-bold mb-1" data-testid="profile-name">{profile.full_name}</h2>
                  <p className="text-sm text-muted-foreground mb-4">{profile.email}</p>
                  
                  {profile.bio && (
                    <p className="text-sm text-center text-muted-foreground mb-4 italic">"{profile.bio}"</p>
                  )}

                  <div className="w-full space-y-2 mt-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Status:</span>
                      <span className={`font-semibold ${profile.subscription_status === 'active' ? 'text-green-500' : 'text-yellow-500'}`}>
                        {profile.subscription_status}
                      </span>
                    </div>
                    {profile.subscription_plan && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Plan:</span>
                        <span className="font-semibold">{profile.subscription_plan}</span>
                      </div>
                    )}
                    {profile.fitness_goal && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Goal:</span>
                        <span className="font-semibold capitalize">{profile.fitness_goal}</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            {progress && (
              <Card className="bg-card/50 border-white/10 mt-4">
                <CardHeader>
                  <CardTitle className="text-lg">Quick Stats</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Total Workouts</span>
                    <span className="text-xl font-bold">{progress.total_workouts}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Last 30 Days</span>
                    <span className="text-xl font-bold">{progress.recent_workouts}</span>
                  </div>
                  {bmi && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">BMI</span>
                      <span className="text-xl font-bold">{bmi}</span>
                    </div>
                  )}
                  {progress.weight_progress && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Weight Change</span>
                      <span className={`text-xl font-bold flex items-center ${progress.weight_progress < 0 ? 'text-green-500' : 'text-yellow-500'}`}>
                        {progress.weight_progress < 0 ? <TrendingDown className="w-5 h-5 mr-1" /> : <TrendingUp className="w-5 h-5 mr-1" />}
                        {Math.abs(progress.weight_progress).toFixed(1)} kg
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 gap-1">
                <TabsTrigger value="info" data-testid="tab-info" className="text-xs sm:text-sm">Personal</TabsTrigger>
                <TabsTrigger value="fitness" data-testid="tab-fitness" className="text-xs sm:text-sm">Fitness</TabsTrigger>
                <TabsTrigger value="progress" data-testid="tab-progress" className="text-xs sm:text-sm">Progress</TabsTrigger>
                <TabsTrigger value="security" data-testid="tab-security" className="text-xs sm:text-sm">Security</TabsTrigger>
              </TabsList>

              {/* Personal Info Tab */}
              <TabsContent value="info" className="mt-4 sm:mt-6">
                <Card className="bg-card/50 border-white/10">
                  <CardHeader className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                      <CardTitle className="text-base sm:text-lg">Personal Information</CardTitle>
                      <Button
                        onClick={() => setEditMode(!editMode)}
                        variant={editMode ? "outline" : "default"}
                        data-testid="edit-profile-btn"
                        size="sm"
                        className="w-full sm:w-auto"
                      >
                        {editMode ? 'Cancel' : 'Edit Profile'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4 p-4 sm:p-6 pt-0 sm:pt-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="full_name" className="text-sm">Full Name</Label>
                        <Input
                          id="full_name"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleInputChange}
                          disabled={!editMode}
                          className="bg-background/50 border-white/10"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          disabled={!editMode}
                          className="bg-background/50 border-white/10"
                        />
                      </div>

                      <div>
                        <Label htmlFor="date_of_birth">Date of Birth</Label>
                        <Input
                          id="date_of_birth"
                          name="date_of_birth"
                          type="date"
                          value={formData.date_of_birth}
                          onChange={handleInputChange}
                          disabled={!editMode}
                          className="bg-background/50 border-white/10"
                        />
                      </div>

                      <div>
                        <Label htmlFor="gender">Gender</Label>
                        <Select
                          value={formData.gender}
                          onValueChange={(value) => handleSelectChange('gender', value)}
                          disabled={!editMode}
                        >
                          <SelectTrigger className="bg-background/50 border-white/10">
                            <SelectValue placeholder="Select gender" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="bio">Bio</Label>
                      <Textarea
                        id="bio"
                        name="bio"
                        value={formData.bio}
                        onChange={handleInputChange}
                        disabled={!editMode}
                        placeholder="Tell us about yourself..."
                        className="bg-background/50 border-white/10"
                        rows={3}
                      />
                    </div>

                    {editMode && (
                      <Button
                        onClick={handleSaveProfile}
                        disabled={loading}
                        className="w-full bg-primary"
                        data-testid="save-profile-btn"
                      >
                        {loading ? 'Saving...' : 'Save Changes'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Fitness Details Tab */}
              <TabsContent value="fitness" className="mt-6">
                <Card className="bg-card/50 border-white/10">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <CardTitle>Fitness Details</CardTitle>
                      <Button
                        onClick={() => setEditMode(!editMode)}
                        variant={editMode ? "outline" : "default"}
                      >
                        {editMode ? 'Cancel' : 'Edit Details'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="height_cm">Height (cm)</Label>
                        <Input
                          id="height_cm"
                          name="height_cm"
                          type="number"
                          value={formData.height_cm}
                          onChange={handleInputChange}
                          disabled={!editMode}
                          className="bg-background/50 border-white/10"
                        />
                      </div>

                      <div>
                        <Label htmlFor="current_weight_kg">Current Weight (kg)</Label>
                        <Input
                          id="current_weight_kg"
                          name="current_weight_kg"
                          type="number"
                          step="0.1"
                          value={formData.current_weight_kg}
                          onChange={handleInputChange}
                          disabled={!editMode}
                          className="bg-background/50 border-white/10"
                        />
                      </div>

                      <div>
                        <Label htmlFor="target_weight_kg">Target Weight (kg)</Label>
                        <Input
                          id="target_weight_kg"
                          name="target_weight_kg"
                          type="number"
                          step="0.1"
                          value={formData.target_weight_kg}
                          onChange={handleInputChange}
                          disabled={!editMode}
                          className="bg-background/50 border-white/10"
                        />
                      </div>

                      <div>
                        <Label htmlFor="fitness_level">Fitness Level</Label>
                        <Select
                          value={formData.fitness_level}
                          onValueChange={(value) => handleSelectChange('fitness_level', value)}
                          disabled={!editMode}
                        >
                          <SelectTrigger className="bg-background/50 border-white/10">
                            <SelectValue placeholder="Select level" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="intermediate">Intermediate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="fitness_goal">Fitness Goal</Label>
                      <Select
                        value={formData.fitness_goal}
                        onValueChange={(value) => handleSelectChange('fitness_goal', value)}
                        disabled={!editMode}
                      >
                        <SelectTrigger className="bg-background/50 border-white/10">
                          <SelectValue placeholder="Select goal" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weight_loss">Weight Loss</SelectItem>
                          <SelectItem value="muscle_gain">Muscle Gain</SelectItem>
                          <SelectItem value="maintenance">Maintenance</SelectItem>
                          <SelectItem value="endurance">Endurance</SelectItem>
                          <SelectItem value="flexibility">Flexibility</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {editMode && (
                      <Button
                        onClick={handleSaveProfile}
                        disabled={loading}
                        className="w-full bg-primary"
                      >
                        {loading ? 'Saving...' : 'Save Changes'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Progress Tab */}
              <TabsContent value="progress" className="mt-6">
                <div className="space-y-6">
                  {/* Log Weight */}
                  <Card className="bg-card/50 border-white/10">
                    <CardHeader>
                      <CardTitle className="flex items-center">
                        <Target className="w-5 h-5 mr-2" />
                        Log Weight
                      </CardTitle>
                      <CardDescription>Track your weight progress over time</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="weight_kg">Weight (kg)</Label>
                          <Input
                            id="weight_kg"
                            type="number"
                            step="0.1"
                            value={weightEntry.weight_kg}
                            onChange={(e) => setWeightEntry({ ...weightEntry, weight_kg: e.target.value })}
                            className="bg-background/50 border-white/10"
                            data-testid="weight-input"
                          />
                        </div>
                        <div>
                          <Label htmlFor="weight_notes">Notes (optional)</Label>
                          <Input
                            id="weight_notes"
                            value={weightEntry.notes}
                            onChange={(e) => setWeightEntry({ ...weightEntry, notes: e.target.value })}
                            placeholder="e.g., Morning weight"
                            className="bg-background/50 border-white/10"
                          />
                        </div>
                      </div>
                      <Button
                        onClick={handleLogWeight}
                        disabled={loading}
                        className="w-full mt-4 bg-primary"
                        data-testid="log-weight-btn"
                      >
                        {loading ? 'Logging...' : 'Log Weight'}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Weight History */}
                  {progress && progress.weight_history.length > 0 && (
                    <>
                      {/* Weight Progress Chart */}
                      <Card className="bg-card/50 border-white/10">
                        <CardHeader>
                          <CardTitle className="flex items-center">
                            <TrendingUp className="w-5 h-5 mr-2" />
                            Weight Progress Chart
                          </CardTitle>
                          <CardDescription>Visual representation of your weight journey</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="h-48 flex items-end gap-1">
                            {progress.weight_history.slice(-20).map((log, idx) => {
                              const weights = progress.weight_history.slice(-20).map(l => l.weight_kg);
                              const minWeight = Math.min(...weights) - 2;
                              const maxWeight = Math.max(...weights) + 2;
                              const range = maxWeight - minWeight;
                              const heightPercent = ((log.weight_kg - minWeight) / range) * 100;
                              
                              return (
                                <div key={idx} className="flex-1 flex flex-col items-center gap-1" title={`${log.weight_kg} kg - ${new Date(log.date).toLocaleDateString()}`}>
                                  <div 
                                    className="w-full bg-gradient-to-t from-primary to-primary/50 rounded-t transition-all hover:from-secondary hover:to-secondary/50"
                                    style={{ height: `${Math.max(heightPercent, 5)}%` }}
                                  ></div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                            <span>{progress.weight_history.slice(-20)[0] && new Date(progress.weight_history.slice(-20)[0].date).toLocaleDateString()}</span>
                            <span>{progress.weight_history.slice(-1)[0] && new Date(progress.weight_history.slice(-1)[0].date).toLocaleDateString()}</span>
                          </div>
                          <div className="flex justify-between mt-1">
                            <span className="text-sm">Min: {Math.min(...progress.weight_history.map(l => l.weight_kg)).toFixed(1)} kg</span>
                            <span className="text-sm">Max: {Math.max(...progress.weight_history.map(l => l.weight_kg)).toFixed(1)} kg</span>
                          </div>
                        </CardContent>
                      </Card>
                      
                      {/* Weight History List */}
                      <Card className="bg-card/50 border-white/10">
                        <CardHeader>
                          <CardTitle className="flex items-center">
                            <Activity className="w-5 h-5 mr-2" />
                            Weight History
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {progress.weight_history.slice().reverse().slice(0, 10).map((log, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between items-center border-b border-white/10 pb-3"
                                data-testid="weight-log-item"
                              >
                                <div>
                                  <p className="font-semibold">{log.weight_kg} kg</p>
                                  <p className="text-sm text-muted-foreground">
                                    {new Date(log.date).toLocaleDateString()} at {new Date(log.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </p>
                                  {log.notes && (
                                    <p className="text-sm text-muted-foreground italic">{log.notes}</p>
                                  )}
                                </div>
                                <Calendar className="w-5 h-5 text-muted-foreground" />
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>
              </TabsContent>

              {/* Security Tab - Change Password */}
              <TabsContent value="security" className="mt-4 sm:mt-6">
                <Card className="bg-card/50 border-white/10">
                  <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="flex items-center text-base sm:text-lg">
                      <Lock className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Change Password
                    </CardTitle>
                    <CardDescription className="text-sm">
                      Update your password to keep your account secure
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 p-4 sm:p-6 pt-0 sm:pt-0">
                    <div>
                      <Label htmlFor="currentPassword" className="text-sm">Current Password</Label>
                      <div className="relative mt-1">
                        <Input
                          id="currentPassword"
                          type={showCurrentPassword ? "text" : "password"}
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                          placeholder="Enter current password"
                          className="bg-background/50 border-white/10 pr-10 h-10 sm:h-12"
                          data-testid="current-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="newPassword" className="text-sm">New Password</Label>
                      <div className="relative mt-1">
                        <Input
                          id="newPassword"
                          type={showNewPassword ? "text" : "password"}
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                          placeholder="Enter new password"
                          className="bg-background/50 border-white/10 pr-10 h-10 sm:h-12"
                          data-testid="new-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="confirmPassword" className="text-sm">Confirm New Password</Label>
                      <div className="relative mt-1">
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                          placeholder="Confirm new password"
                          className="bg-background/50 border-white/10 pr-10 h-10 sm:h-12"
                          data-testid="confirm-password-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      onClick={handleChangePassword}
                      disabled={changingPassword}
                      className="w-full bg-primary h-10 sm:h-12"
                      data-testid="change-password-btn"
                    >
                      {changingPassword ? 'Changing Password...' : 'Change Password'}
                    </Button>

                    <p className="text-xs text-muted-foreground text-center">
                      Password must be at least 6 characters long
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
